import loadModuleConfigs from '@modules/core/config/modules';

import type {
  TApplicationRoute,
  TCategorizedRoutes,
  TMenuItemEntry,
  TModule,
  TPermissions,
  TRouteEntry,
} from '@modules/core/types';


/**
 * A singleton registry that manages application modules, their routes, and menu items.
 *
 * The AppModuleRegistry class ensures that only one instance of the registry exists throughout the application.
 * It allows for registering and unregistering modules, and provides methods to retrieve accessible routes and menu items
 * based on user permissions. The registry supports permission inheritance, enabling parent module permissions to be
 * propagated to their routes when applicable.
 *
 * Key Features:
 * - Singleton pattern implementation via a private constructor and a getInstance() method.
 * - Module registration and unregistration with error handling for duplicate or missing modules.
 * - Route extraction (getRoutes) that filters and processes routes according to user permissions,
 *   including the management of fallback routes.
 * - Menu item extraction (getMenuItems) which retrieves navigation items from registered module routes.
 * - Permission-checking logic that evaluates user access using roles and scopes, with support for merging
 *   parent and child permissions via inheritance.
 *
 * Note: The types TModule, TRouteEntry, TApplicationRoute, TMenuItemEntry, and TPermissions are assumed to be defined
 * elsewhere in the codebase.
 */
export class AppModuleRegistry {
  private static instance: AppModuleRegistry;

  /**
   * Singleton instance of ModuleRegistry
   * @private
   */
  private readonly modules: Map<string, TModule>;

  private defaultFallbackRoute = '/';

  private menuCache: Map<string, TMenuItemEntry[]> = new Map();

  private routeCache: { routes: TRouteEntry[]; fallbacks: string[] } | null = null;

  // Cache for routes by permission key
  private permissionRouteCache: Map<string, { routes: TRouteEntry[]; fallbacks: string[] }> =
    new Map();

  // Cache for categorized routes (public vs protected)
  private categorizedRouteCache: Map<string, TCategorizedRoutes> = new Map();

  /**
   * Private constructor to prevent instantiation from outside the class.
   * Initializes the modules map.
   */
  private constructor() {
    this.modules = new Map<string, TModule>();
  }

  /**
   * Returns the singleton instance of ModuleRegistry.
   * @returns The singleton instance of ModuleRegistry.
   */
  static getInstance(): AppModuleRegistry {
    if (!AppModuleRegistry.instance) {
      AppModuleRegistry.instance = new AppModuleRegistry();
    }
    return AppModuleRegistry.instance;
  }

  /**
   * Registers a module with the specified name and module object.
   * @param name - The name of the module to register.
   * @param module - The module object to register.
   * @throws Error if the module is already registered.
   */
  register(name: string, module: TModule): void {
    if (this.modules.has(name)) {
      throw new Error(`Module ${name} is already registered.`);
    }

    // Validate the module parameter to ensure it conforms to the TModule type
    if (!module || typeof module !== 'object' || !module.routes || !Array.isArray(module.routes)) {
      throw new Error(`Invalid module provided for registration: ${name}`);
    }
    this.modules.set(name, module);
    this.resetCache();
  }

  /**
   * Unregisters a module with the specified name.
   * @param name - The name of the module to unregister.
   * @throws Error if the module is not registered.
   */
  unregister(name: string): void {
    if (name == null) {
      throw new Error('Module name cannot be null or undefined.');
    }
    if (!this.modules.has(name)) {
      throw new Error(`Module ${name} is not registered.`);
    }
    this.modules.delete(name);
    this.resetCache();
  }

  /**
   * Checks if a module with the specified name is registered.
   *
   * @param name - The name of the module to check.
   * @returns True if the module is registered; otherwise, false.
   */
  isRegistered(name: string): boolean {
    return this.modules.has(name);
  }

  /**
   * Returns modules the user can access along with their fallback routes.
   * This is useful for building dashboards or navigation menus without exposing
   * the internal modules map.
   */
  getAccessibleModules(permission: TPermissions): Array<{
    id: string;
    fallbackRoute: string;
    routes: Array<{ id: string; path: string; title?: string; icon?: string }>;
  }> {
    const accessible: Array<{
      id: string;
      fallbackRoute: string;
      routes: Array<{ id: string; path: string; title?: string; icon?: string }>;
    }> = [];
    const hiddenModuleIds = new Set(['home', 'public']);

    this.modules.forEach((module) => {
      if (hiddenModuleIds.has(module.id)) {
        return;
      }

      if (this.canAccessModule(module, permission) || module.isPublic) {
        const fallbackRoute =
          module.fallbackRoute && module.fallbackRoute.length > 0 ? module.fallbackRoute[0] : null;

        if (fallbackRoute) {
          const routes = module.routes
            .filter(
              (route) => !route.permissions || this.checkPermissions(route.permissions, permission)
            )
            .filter((route) => route.menu)
            .map((route) => ({
              id: route.id,
              path: route.path,
              title: route.menu?.title ?? route.id,
              icon: route.menu?.icon,
            }));

          if (routes.length > 0) {
            accessible.push({
              id: module.id,
              fallbackRoute,
              routes,
            });
          }
        }
      }
    });

    return accessible;
  }
  /**
   * Gets menu items from all registered modules based on user permissions
   * @param permission - User permissions
   * @returns Menu items organized by groups
   */
  getMenuItems(permission: TPermissions): TMenuItemEntry[] {
    const permKey = this.generatePermissionKey(permission);
    const cacheKey = `menu_${permKey}`;
    const cached = this.menuCache.get(cacheKey);
    if (cached) {
      return cached;
    }
    const menuItems: TMenuItemEntry[] = [];

    this.modules.forEach((module) => {
      if (this.canAccessModule(module, permission)) {
        const extractedMenuItems = this.extractMenuItems(
          module.routes,
          permission,
          module.permissions
        );
        menuItems.push(...extractedMenuItems);
      }
    });

    this.menuCache.set(cacheKey, menuItems);
    return menuItems;
  }

  /**
   * Gets routes from all registered modules that match the user permissions.
   * Also handles permission inheritance from parent routes to subroutes.
   *
   * @param permission - The permissions of the current user
   * @returns Array of route entries that the user has permission to access
   */
  getRoutes(permission: TPermissions): { routes: TRouteEntry[]; fallbacks: string[] } {
    // Return cached result if available
    if (this.routeCache) {
      return this.routeCache;
    }

    // Generate a cache key based on permission
    const permKey = this.generatePermissionKey(permission);

    // Check if we have a cached result for this permission key
    const cachedRoutes = this.permissionRouteCache.get(permKey);
    if (cachedRoutes) {
      this.routeCache = cachedRoutes;
      return cachedRoutes;
    }

    const routes: TRouteEntry[] = [];
    let fallbacks: string[] = [];

    // Use array methods instead of forEach for better performance
    const accessibleModules = Array.from(this.modules.values()).filter((module) =>
      this.canAccessModule(module, permission)
    );

    // Process all accessible modules
    accessibleModules.forEach((module) => {
      // Process routes with inheritance support
      this.processRoutes(routes, module.routes, permission, module.permissions);

      // Add fallback routes
      if (module.fallbackRoute && module.fallbackRoute.length > 0) {
        fallbacks.push(...module.fallbackRoute);
      }
    });

    // Optimize fallbacks processing
    if (routes.length > 0) {
      // If routes are defined, filter fallbacks to ensure they are valid paths
      const validPaths = new Set(routes.map((route) => route.path));
      fallbacks = fallbacks.filter((path) => validPaths.has(path));
    } else {
      fallbacks = []; // Clear fallbacks if no routes are defined
    }

    // If no fallbacks are defined, add a default fallback route
    if (fallbacks.length === 0) {
      fallbacks.push(this.defaultFallbackRoute); // Default fallback route
    }

    const result = { routes, fallbacks };

    // Cache the result
    this.routeCache = result;
    this.permissionRouteCache.set(permKey, result);

    return result;
  }

  /**
   * Gets categorized routes (public vs protected) from all registered modules.
   * Public routes don't require authentication, while protected routes do.
   * This method is optimized and caches results.
   *
   * @param permission - The permissions of the current user
   * @returns Object containing categorized routes and fallbacks
   */
  getCategorizedRoutes(permission: TPermissions): TCategorizedRoutes {
    // Generate cache key for this permission set
    const permKey = this.generatePermissionKey(permission);

    // Check if we have cached categorized routes
    const cacheKey = `categorized_${permKey}`;
    if (this.categorizedRouteCache.has(cacheKey)) {
      return this.categorizedRouteCache.get(cacheKey)!;
    }

    const publicRoutes: TRouteEntry[] = [];
    const protectedRoutes: TRouteEntry[] = [];
    let fallbacks: string[] = [];

    // Process all modules
    Array.from(this.modules.values()).forEach((module) => {
      const isModulePublic = module.isPublic === true;

      // Process routes
      module.routes.forEach((route) => {
        const isRoutePublic = isModulePublic || route.isPublic === true;

        if (isRoutePublic) {
          // Add to public routes (no permission check needed)
          publicRoutes.push({
            id: route.id,
            path: route.path,
            component: route.component,
            exact: route.exact ?? true,
            isPublic: true,
            requiresAuth: false,
            layout: route.layout,
          });
        } else {
          // Check permissions for protected routes
          const effectivePermissions = this.resolveEffectivePermissions(
            route.permissions,
            module.permissions,
            route.inheritPermissions ?? true
          );

          protectedRoutes.push({
            id: route.id,
            path: route.path,
            component: route.component,
            exact: route.exact ?? true,
            isPublic: false,
            requiresAuth: true,
            layout: route.layout,
            requiredPermissions: effectivePermissions,
          });
        }
      });

      // Add fallback routes if module is accessible
      if (isModulePublic || this.canAccessModule(module, permission)) {
        if (module.fallbackRoute && module.fallbackRoute.length > 0) {
          fallbacks.push(...module.fallbackRoute);
        }
      }
    });

    // Optimize fallbacks processing
    const allRoutes = [...publicRoutes, ...protectedRoutes];
    if (allRoutes.length > 0) {
      const validPaths = new Set(allRoutes.map((route) => route.path));
      fallbacks = fallbacks.filter((path) => validPaths.has(path));
    } else {
      fallbacks = [];
    }

    // If no fallbacks are defined, add a default fallback route
    if (fallbacks.length === 0) {
      fallbacks.push(this.defaultFallbackRoute);
    }

    const result = {
      publicRoutes,
      protectedRoutes,
      fallbacks,
    };

    // Cache the result
    this.categorizedRouteCache.set(cacheKey, result);

    return result;
  }

  /**
   * Extracts menu items from routes
   * @param routes - Routes to extract menu items from
   * @param userPermissions - User permissions
   * @param parentPermissions - Parent permissions for inheritance
   * @returns Array of menu items
   */
  private extractMenuItems(
    routes: TApplicationRoute[],
    userPermissions: TPermissions,
    parentPermissions?: TPermissions
  ): TMenuItemEntry[] {
    const menuItems: TMenuItemEntry[] = [];

    routes.forEach((route) => {
      // Determine effective permissions with inheritance
      const effectivePermissions = this.resolveEffectivePermissions(
        route.permissions,
        parentPermissions,
        route.inheritPermissions ?? true
      );
      // Check if user has access based on effective permissions
      if (effectivePermissions && this.checkPermissions(effectivePermissions, userPermissions)) {
        if (route.menu) {
          // Build a menu item
          const menuItem: TMenuItemEntry = {
            id: route.menu.id || route.id,
            path: route.path,
            title: route.menu.title,
            icon: route.menu.icon,
            activePaths: route.menu.associatedPaths,
          };

          menuItems.push(menuItem);
        }
      }
    });

    return menuItems;
  }

  /**
   * Checks if a module is accessible based on user permissions.
   * @param module - The module to check
   * @param permission - The user's permissions
   * @returns True if the module is accessible; otherwise, false
   */
  private canAccessModule(module: TModule, permission: TPermissions): boolean {
    if (!module.permissions) {
      return true;
    }

    if (module.permissions.scopes && module.permissions.scopes.length > 0) {
      return this.checkPermissions(module.permissions, permission);
    }

    const routesWithScopes = module.routes.filter(
      (route) => route.permissions?.scopes && route.permissions.scopes.length > 0
    );

    if (routesWithScopes.length > 0) {
      return routesWithScopes.some(
        (route) => this.checkPermissions(route.permissions!, permission)
      );
    }

    return this.checkPermissions(module.permissions, permission);
  }

  /**
   * Process routes recursively with permission inheritance support
   * @param result - Array to store resulting routes
   * @param routes - Routes to process
   * @param userPermissions - User permissions
   * @param parentPermissions - Parent permissions for inheritance
   */
  private processRoutes(
    result: TRouteEntry[],
    routes: TApplicationRoute[],
    userPermissions: TPermissions,
    parentPermissions: TPermissions | undefined
  ): void {
    routes.forEach((route) => {
      // Determine effective permissions with inheritance
      const effectivePermissions = this.resolveEffectivePermissions(
        route.permissions,
        parentPermissions,
        route.inheritPermissions ?? true
      );

      // Check if user has access based on effective permissions
      if (effectivePermissions && this.checkPermissions(effectivePermissions, userPermissions)) {
        // Add the route
        result.push({
          id: route.id,
          path: route.path,
          component: route.component,
          exact: route.exact ?? true,
          isPublic: route.isPublic,
          requiresAuth: !route.isPublic,
          layout: route.layout,
        });
      }
    });
  }

  /**
   * Checks if a user has a specific permission
   * @param userPermissions - User's permissions
   * @param permission - Permission to check
   * @returns True if the user has the permission; otherwise, false
   */
  hasPermission(userPermissions: TPermissions, permission: TPermissions): boolean {
    return this.checkPermissions(permission, userPermissions);
  }

  /**
   * Resolves effective permissions considering inheritance
   * @param currentPermissions - Direct permissions
   * @param parentPermissions - Parent route or module permissions
   * @param allowInheritance - Flag to allow/disallow inheritance
   * @returns Effective permissions after inheritance resolution
   */
  private resolveEffectivePermissions(
    currentPermissions: TPermissions | undefined,
    parentPermissions: TPermissions | undefined,
    allowInheritance: boolean
  ): TPermissions | undefined {
    if (!allowInheritance) return currentPermissions;

    if (!currentPermissions && !parentPermissions) return undefined;
    if (!currentPermissions) return parentPermissions;
    if (!parentPermissions) return currentPermissions;

    // Merge permissions
    return {
      roles: [...(currentPermissions.roles || []), ...(parentPermissions.roles || [])].filter(
        (role, index, self) => self.indexOf(role) === index
      ), // Remove duplicates

      scopes: [...(currentPermissions.scopes || []), ...(parentPermissions.scopes || [])].filter(
        (scope, index, self) => self.indexOf(scope) === index
      ), // Remove duplicates
    };
  }

  /**
   * Checks if a user has permissions required by a module or route
   * @param requiredPermissions - Required permissions
   * @param userPermissions - User's permissions
   * @returns True if the user has the required permissions
   */
  private checkPermissions(
    requiredPermissions: TPermissions,
    userPermissions: TPermissions
  ): boolean {
    let canAccess = false;
    // Check roles if required roles exist in requiredPermissions.
    if (requiredPermissions.roles && requiredPermissions.roles.length > 0) {
      if (!userPermissions.roles || userPermissions.roles.length === 0) {
        return false;
      }
      const userRolesSet = new Set(userPermissions.roles);
      canAccess = requiredPermissions.roles.some((role) => userRolesSet.has(role));
    } else {
      canAccess = true; // If no required roles are defined, assume access is granted.
    }

    // Check scopes
    if (requiredPermissions.scopes && requiredPermissions.scopes.length > 0) {
      if (!userPermissions.scopes || userPermissions.scopes.length === 0) {
        return false;
      }
      const userScopesSet = new Set(userPermissions.scopes);
      canAccess = canAccess && requiredPermissions.scopes.some((scope) => userScopesSet.has(scope));
    }

    return canAccess;
  }

  /**
   * Generates a unique key for caching routes based on permissions
   * @param permission - User permissions
   * @returns A string key representing the permissions
   */
  private permissionKeyCache: Map<string, string> = new Map();

  /**
   * Generates a unique key for caching routes based on permissions
   * @param permission - User permissions
   * @returns A string key representing the permissions
   */
  private generatePermissionKey(permission: TPermissions): string {
    const cacheKey = JSON.stringify(permission);
    if (this.permissionKeyCache.has(cacheKey)) {
      const cachedValue = this.permissionKeyCache.get(cacheKey);
      if (cachedValue !== undefined) {
        return cachedValue;
      }
    }
    const roles = (permission.roles ?? []).join('_') || '';
    const scopes = (permission.scopes ?? []).join('_') || '';
    const generatedKey = `${roles}|${scopes}`;

    this.permissionKeyCache.set(cacheKey, generatedKey);
    return generatedKey;
  }

  /**
   * Resets the internal caches used for routes, menus, and permissions.
   *
   * This method clears cached data related to:
   * - Route configurations (routeCache)
   * - Menu items (menuCache)
   * - Permission-based routes (permissionRouteCache)
   * - Permission key mappings (permissionKeyCache)
   *
   * Use this function when you need to refresh cached application state to ensure
   * that subsequent operations have access to the most up-to-date data.
   *
   * @returns {void} No return value.
   */
  resetCache(): void {
    this.routeCache = null;
    this.menuCache.clear();
    this.permissionRouteCache.clear();
    this.permissionKeyCache.clear();
    this.categorizedRouteCache.clear();
  }
}

/**
 * Asynchronously initializes application modules.
 *
 * This function loads the module configurations by calling `loadModuleConfigs`,
 * retrieves a singleton instance of the `AppModuleRegistry`, and registers each module
 * with its corresponding configurations.
 *
 * @async
 * @returns {Promise<AppModuleRegistry>} A promise that resolves to the application's module registry after all modules have been registered.
 */
export const registerAppModules = async () => {
  const configModules = await loadModuleConfigs();
  const registry = AppModuleRegistry.getInstance();

  Object.entries(configModules).forEach(([moduleName, configs]) => {
    registry.register(moduleName, configs);
  });
  return registry;
};
